import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import AdvanceShareStyleVersionForm from './advance-share-style-version-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { AdvanceShareStyleVersion } from '@/models/advance-share-style-version.model';
import { getAdvanceShareStyleVersion } from '@/services/advance-share-style-version.service';
import PageHeader from '@/components/PageHeader';
import AdvanceShareStyleVersionActions from './actions/advance-share-style-version-actions';
import AdvanceShareStyleVersionPromptGuide from './advance-share-style-version-prompt-guide';
import AdvanceShareStyleVersionUsageRules from './advance-share-style-version-usage-rules';
import AdvanceShareStyleVersionPolicies from './advance-share-style-version-policies';
import AdvanceShareList from '@/pages/advance-share/list/advance-share-list';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect} >
      <Nav.Item eventKey="version-form">Style Version</Nav.Item>
      <Nav.Item eventKey="version-policies" disabled={newMode}>Policies</Nav.Item>
      <Nav.Item eventKey="version-usage-rules" disabled={newMode}>Usage Rules</Nav.Item>
      <Nav.Item eventKey="version-actions" disabled={newMode}>Actions</Nav.Item>
      <Nav.Item eventKey="version-prompt-guide" >Prompt Guide</Nav.Item>
      <Nav.Item eventKey="version-generate-requests" disabled={newMode}>Generate Requests</Nav.Item>
    </Nav>
  );
};

const Page = () => {
  const styleId = useLocation()?.state?.styleId;
  const requestedTab = useLocation()?.state?.tab;  
  
  const [activeTab, setActiveTab] = useState('version-form');
  const [formObject, setFormObject] = useState<AdvanceShareStyleVersion|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;

  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<AdvanceShareStyleVersion> = await getAdvanceShareStyleVersion(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Style Version: ${errorModel?.errorMessage}`);
        nav('/advance-shares/styles');
      }
      finally {
        setLoading(false);
      }
    }
  };

  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit ? `Edit Version` : 'New Version'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item >Advance Shares</Breadcrumb.Item>
            <Breadcrumb.Item href='/advance-shares/styles'>Styles</Breadcrumb.Item>
            <Breadcrumb.Item href='#' onClick={() => nav(`/advance-shares/styles/${formObject?.styleId ?? styleId}`, {state:{tab: "style-versions"}})} >Versions</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" 
        active={activeTab} 
        onSelect={key => setActiveTab(key)} 
        newMode={pageState == PageState.New} 
      />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'version-form' && <AdvanceShareStyleVersionForm styleId={styleId} formObject={formObject} pageState={pageState}/>}
          {activeTab == 'version-policies' && <AdvanceShareStyleVersionPolicies formObject={formObject} fetchData={fetchData}/>}
          {activeTab == 'version-usage-rules' && <AdvanceShareStyleVersionUsageRules formObject={formObject} fetchData={fetchData}/>}
          {activeTab == 'version-actions' && <AdvanceShareStyleVersionActions formObject={formObject} fetchData={fetchData} />}
          {activeTab == 'version-prompt-guide' && <AdvanceShareStyleVersionPromptGuide />}
          {activeTab == 'version-generate-requests' && <AdvanceShareList styleVersionId={formObject?.id} />}
        </>
      }
    </Panel>
  );
};

export default Page;
